/**
 * Track D — main-thread client for the pixelate worker. Wraps postMessage/
 * transferables, job ids, progress callbacks, and cancellation.
 *
 * Cancellation is two-tier: we first POST a CancelRequest so the worker can bail
 * cooperatively between stages (and stay alive for reuse), then hard-respawn as a
 * fallback so a wedged synchronous run can never block the next preview.
 */
import type {
  CancelRequest,
  PhotoFillResult,
  PhotoFillSettings,
  PixelResult,
  PixelSettings,
  PixelateRequest,
  PixelateResponse,
  PolyGeom,
  RawImage,
} from "../core/types";

export interface JobProgress {
  stage: string;
  fraction: number;
  pixelCount: number;
}

interface PendingJob {
  id: number;
  resolve: (value: { result: PixelResult; photoFill?: PhotoFillResult }) => void;
  reject: (reason: Error) => void;
  onProgress?: (p: JobProgress) => void;
}

export class PixelateClient {
  private worker: Worker | null = null;
  private jobId = 0;
  private pending: PendingJob | null = null;

  private ensureWorker(): Worker {
    if (!this.worker) {
      this.worker = new Worker(new URL("./pixelateWorker.ts", import.meta.url), { type: "module" });
      this.worker.onmessage = (e: MessageEvent) => this.handleMessage(e.data as PixelateResponse);
      this.worker.onerror = (e: ErrorEvent) => this.handleError(e.message || "Pixelate worker error");
      // A structured-clone failure surfaces here instead of as a normal message;
      // reject the pending job rather than hang forever, and respawn the worker.
      this.worker.onmessageerror = () => this.handleError("Pixelate worker message could not be read.");
    }
    return this.worker;
  }

  private respawn(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
  }

  /** POST a cooperative cancel for the given job so the (live) worker can bail. */
  private postCancel(jobId: number): void {
    if (this.worker) {
      const cancel: CancelRequest = { type: "cancel", jobId };
      this.worker.postMessage(cancel);
    }
  }

  run(
    geoms: readonly PolyGeom[],
    selectedLayers: ReadonlySet<number>,
    settings: PixelSettings,
    options: {
      softLimit: boolean;
      maxPixelsOverride?: number;
      photoFill?: { settings: PhotoFillSettings; image: RawImage };
      onProgress?: (p: JobProgress) => void;
    },
  ): Promise<{ result: PixelResult; photoFill?: PhotoFillResult }> {
    // Supersede any in-flight job: reject its promise and abandon the (possibly
    // long, synchronous) worker computation by respawning, so the new preview is
    // not stuck behind stale work.
    if (this.pending) {
      const prev = this.pending;
      this.pending = null;
      this.postCancel(prev.id);
      prev.reject(new Error("Pixelate job superseded"));
      this.respawn();
    }

    const id = ++this.jobId;
    const worker = this.ensureWorker();
    // Geometry is small (many little rings); structured clone WITHOUT transfer
    // keeps the caller's Float64Array buffers intact.
    const req: PixelateRequest = {
      type: "pixelate",
      jobId: id,
      geoms: geoms.map((g) => ({ layer: g.layer, points: g.points, closed: g.closed, source: g.source })),
      selectedLayers: [...selectedLayers],
      settings,
      softLimit: options.softLimit,
      maxPixelsOverride: options.maxPixelsOverride,
      photoFill: options.photoFill,
    };

    return new Promise((resolve, reject) => {
      this.pending = { id, resolve, reject, onProgress: options.onProgress };
      worker.postMessage(req);
    });
  }

  /**
   * Cancel the in-flight job. Posts a cooperative CancelRequest first (so a
   * cooperative worker can bail and be reused), then hard-respawns as a fallback
   * for a run wedged in a synchronous stage. Pending run() rejects.
   */
  cancel(): void {
    if (this.pending) {
      const p = this.pending;
      this.pending = null;
      this.postCancel(p.id);
      p.reject(new Error("Pixelate cancelled"));
    }
    this.respawn();
  }

  /**
   * Tear down the client and its worker (e.g. from a pagehide handler). Rejects
   * any pending job and terminates the worker; the client is unusable afterwards
   * only until the next run(), which lazily respawns.
   */
  dispose(): void {
    if (this.pending) {
      const p = this.pending;
      this.pending = null;
      p.reject(new Error("Pixelate client disposed"));
    }
    this.respawn();
  }

  private handleMessage(msg: PixelateResponse): void {
    const pending = this.pending;
    if (!pending || msg.jobId !== pending.id) return; // stale (superseded) response
    switch (msg.type) {
      case "progress":
        pending.onProgress?.({ stage: msg.stage, fraction: msg.fraction, pixelCount: msg.pixelCount });
        break;
      case "done":
        this.pending = null;
        pending.resolve({ result: msg.result, photoFill: msg.photoFill });
        break;
      case "error":
        this.pending = null;
        pending.reject(new Error(msg.message));
        break;
      case "cancelled":
        this.pending = null;
        pending.reject(new Error("Pixelate cancelled"));
        break;
    }
  }

  private handleError(message: string): void {
    const pending = this.pending;
    // A crashed worker must never be reused: respawn regardless of pending state.
    this.respawn();
    if (!pending) return;
    this.pending = null;
    pending.reject(new Error(message));
  }
}
